import React, { useEffect, useRef, useState } from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Pagination, Autoplay } from 'swiper/modules';
import * as XLSX from 'xlsx';

import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';

const LinkedInPostCard = ({ embedCode, onUserInteract }) => (
  <div
    className="h-full overflow-hidden rounded-lg bg-white shadow-[0_4px_16px_rgba(0,0,0,0.1)] transition-transform duration-300 hover:-translate-y-1"
    onPointerDownCapture={onUserInteract}
    onTouchStartCapture={onUserInteract}
    onMouseDownCapture={onUserInteract}
  >
    <div className="relative overflow-hidden">
      <div className="linkedin-embed-wrapper block h-full w-full overflow-hidden" dangerouslySetInnerHTML={{ __html: embedCode }} />
    </div>
  </div>
);

export const RecentPosts = () => {
  const [linkedInPosts, setLinkedInPosts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(false);
  const swiperRef = useRef(null);

  const stopAutoplay = () => {
    if (swiperRef.current?.autoplay?.running) {
      swiperRef.current.autoplay.stop();
    }
  };

  useEffect(() => {
    const excelURL =
      'https://fintechglobalcenter-my.sharepoint.com/personal/keyur_thakkar_fintechglobal_center/_layouts/15/download.aspx?share=Ede3S3XTfmlGsto-9ocPgWABaT_ej3PDUvp3pQxbDJn4dA';
    const excelUrlWithBypass = `${excelURL}&cb=${Date.now()}`;

    setIsLoading(true);
    fetch(excelUrlWithBypass, {
      cache: 'no-store',
      headers: {
        'Cache-Control': 'no-cache',
        Pragma: 'no-cache',
      },
    })
      .then((res) => {
        if (!res.ok) throw new Error('Failed to fetch Excel file');
        return res.arrayBuffer();
      })
      .then((arrayBuffer) => {
        const workbook = XLSX.read(arrayBuffer, { type: 'array' });
        const sheetName = workbook.SheetNames[1];
        const worksheet = workbook.Sheets[sheetName];
        const data = XLSX.utils.sheet_to_json(worksheet, { header: 1 });

        const posts = data
          .flat()
          .filter((cell) => typeof cell === 'string' && cell.includes('linkedin.com'))
          .map((url) => {
            const match = url.match(/\/update\/(urn:li:[\w:]+)/);
            if (!match) return null;
            return {
              embedCode: `<iframe class="linkedin-embed-frame" src="https://www.linkedin.com/embed/feed/update/${match[1]}?collapsed=1" frameborder="0" allowfullscreen scrolling="yes" title="LinkedIn post" style="display:block;width:100%;max-width:100%;min-height:399px;border:none;overflow:auto;"></iframe>`,
            };
          })
          .filter(Boolean);

        setLinkedInPosts(posts);
        setIsLoading(false);
      })
      .catch((err) => {
        console.error('Error fetching posts:', err);
        setError(true);
        setIsLoading(false);
      });
  }, []);

  return (
    <section id="recent-posts" className="px-4 py-20 md:px-8 bg-[#F1F1F1]">
      <div className="mx-auto w-full max-w-7xl">
        <header className="pb-2 text-center">
          <h2 className="font-oswald text-black text-3xl md:text-5xl font-bold uppercase mb-6 tracking-tight">Our Recent Posts</h2>
        </header>

        {isLoading ? (
          <div className="flex h-[300px] min-h-[300px] w-full flex-col items-center justify-center">
            <div className="text-center">
              <div className="mx-auto mb-3 h-12 w-12 animate-spin rounded-full border-4 border-[#1d2027] border-r-transparent" role="status" aria-label="Loading" />
              <p className="font-semibold text-slate-300">Loading recent LinkedIn posts...</p>
            </div>
          </div>
        ) : error ? (
          <div className="py-5 text-center font-semibold text-red-400">
            <p>Unable to load LinkedIn posts at the moment. Please try again later.</p>
          </div>
        ) : linkedInPosts && linkedInPosts.length === 0 ? (
          <div className="py-5 text-center text-slate-300">
            <p>No LinkedIn posts available at the moment.</p>
          </div>
        ) : (
          <div className="pt-3 pb-8">
            <Swiper
              modules={[Navigation, Pagination, Autoplay]}
              spaceBetween={20}
              slidesPerView={1}
              centeredSlides={true}
              centeredSlidesBounds={true}
              loop={false}
              navigation={false}
              pagination={{ clickable: true }}
              speed={900}
              autoplay={{
                delay: 9000,
                disableOnInteraction: true,
                pauseOnMouseEnter: true,
              }}
              onSwiper={(swiper) => {
                swiperRef.current = swiper;
              }}
              onTouchStart={stopAutoplay}
              onClick={stopAutoplay}
              breakpoints={{
                640: {
                  slidesPerView: 1,
                },
                768: {
                  slidesPerView: 2,
                },
                1024: {
                  slidesPerView: 3,
                },
              }}
              style={{
                paddingBottom: '44px',
                '--swiper-pagination-color': '#1d2027',
                '--swiper-pagination-bullet-inactive-color': '#1d2027',
                '--swiper-pagination-bullet-inactive-opacity': '0.3',
                '--swiper-pagination-bottom': '6px',
              }}
              className="linkedin-posts-swiper"
            >
              {linkedInPosts.map((post, index) => (
                <SwiperSlide key={index}>
                  <LinkedInPostCard embedCode={post.embedCode} onUserInteract={stopAutoplay} />
                </SwiperSlide>
              ))}
            </Swiper>
          </div>
        )}

        <div className="text-center" data-aos="fade-up">
          <a
            href="https://www.linkedin.com/company/fintechglobalcenter/posts"
            target="_blank"
            rel="noopener noreferrer"
            className="mt-4 inline-flex items-center justify-center rounded-md border border-gray-300 bg-gray-200 px-6 py-3 font-montserrat text-sm font-semibold text-black transition hover:bg-white/20"
          >
            <span>View all Posts</span>
          </a>
        </div>
      </div>
    </section>
  );
};

export default RecentPosts;
